import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { ColorPicker, Input, Button, Form, Card, Space, Spin, Alert, Typography } from 'antd';
import type { Color as AntColor } from 'antd/es/color-picker';
import { toast } from 'react-toastify';

const { Title } = Typography;

interface ColorData {
  _id: string;
  name: string;
  firmCode?: string;
  tcxCode?: string;
  hexCode?: string;
  notes?: string;
}

const EditColor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission, isAdmin } = useContext(AuthContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true); // Veri yükleme için
  const [saving, setSaving] = useState(false); // Kaydetme için
  const [error, setError] = useState<string | null>(null);
  const [currentColorHex, setCurrentColorHex] = useState<string>('FFFFFF'); // Varsayılan beyaz

  useEffect(() => {
    if (!isAdmin && !hasPermission('colors', 'update')) {
      navigate('/unauthorized');
      return;
    }
    // fetchColor'ı burada tanımlayıp çağırıyoruz.
    const loadColorData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!id) {
          setError("Renk ID bulunamadı.");
          setLoading(false);
          return;
        }
        const response = await api.get(`/colors/${id}`);
        const colorData: ColorData = response.data;
        const initialHex = colorData.hexCode || 'FFFFFF';
        
        form.setFieldsValue({
          name: colorData.name,
          firmCode: colorData.firmCode || '',
          tcxCode: colorData.tcxCode || '',
          hexCode: colorData.hexCode || '', // Formdaki hexCode'u ayarla
          notes: colorData.notes || ''
        });
        setCurrentColorHex(initialHex); // ColorPicker için state'i ayarla
        
      } catch (err: any) {
        setError(err.response?.data?.message || 'Renk bilgileri yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };
    loadColorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAdmin, hasPermission, navigate, form]); // form'u bağımlılıklara ekledik

  // fetchColor fonksiyonu kaldırıldı, useEffect içinde hallediliyor.

  const handleColorPickerChange = (colorValue: AntColor, hex?: string) => { // hex parametresini opsiyonel yapalım
    let effectiveHex: string | undefined = undefined;

    if (colorValue && typeof colorValue.toHexString === 'function') {
      try {
        effectiveHex = colorValue.toHexString();
        // console.log('[EditColor - ColorPicker] colorValue.toHexString() sonucu:', effectiveHex);
      } catch (e) {
        console.error('[EditColor - ColorPicker] colorValue.toHexString() çağrılırken hata:', e);
      }
    }
    
    if (!effectiveHex && typeof hex === 'string' && hex && hex !== '#') {
        // console.log('[EditColor - ColorPicker] colorValue.toHexString() başarısız, hex parametresi kullanılıyor:', hex);
        effectiveHex = hex;
    }

    if (typeof effectiveHex !== 'string' || !effectiveHex || effectiveHex === '#') {
      console.warn('[EditColor - ColorPicker] Geçerli bir hex değeri alınamadı. Gelen hex:', hex, 'colorValue:', colorValue);
      setCurrentColorHex('FFFFFF');
      form.setFieldsValue({ hexCode: '' });
      return;
    }

    let pureHex = effectiveHex.startsWith('#') ? effectiveHex.substring(1) : effectiveHex;
    pureHex = pureHex.toUpperCase();

    if (pureHex.length === 8) {
      pureHex = pureHex.substring(0, 6);
    } else if (pureHex.length === 3) {
      pureHex = pureHex.split('').map(char => char + char).join('');
    }

    if (/^[0-9A-F]{6}$/.test(pureHex)) {
      setCurrentColorHex(pureHex);
      form.setFieldsValue({ hexCode: pureHex });
    } else {
      console.warn(`[EditColor - ColorPicker] İşlenmiş hex geçerli değil: "${pureHex}". Orijinal hex: "${hex}", effectiveHex: "${effectiveHex}". Varsayılana dönülüyor.`);
      setCurrentColorHex('FFFFFF');
      form.setFieldsValue({ hexCode: '' });
    }
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    const dataToSubmit = {
      name: values.name,
      firmCode: values.firmCode || '',
      tcxCode: values.tcxCode || '',
      hexCode: values.hexCode || currentColorHex,
      notes: values.notes || ''
    };

    try {
      await api.put(`/colors/${id}`, dataToSubmit);
      toast.success('Renk başarıyla güncellendi');
      navigate('/colors');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Renk güncellenirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin tip="Renk bilgileri yükleniyor..." size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert message="Hata" description={error} type="error" showIcon action={
          <Button onClick={loadColorData}>Tekrar Dene</Button>
        }/>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={3} className="mb-0">Renk Düzenle</Title>
        <Button onClick={() => navigate('/colors')}>
          Geri Dön
        </Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Card title="Temel Bilgiler" bordered={false} className="mb-6">
          <Form.Item
            name="name"
            label="Renk Adı"
            rules={[{ required: true, message: 'Lütfen renk adını girin!' }]}
          >
            <Input placeholder="Örn: Ateş Kırmızısı" />
          </Form.Item>

          <Form.Item
            name="firmCode"
            label="Firma Kodu (Opsiyonel)"
          >
            <Input placeholder="Firmaya özel renk kodu" />
          </Form.Item>
        </Card>

        <Card title="Renk Tanımları" bordered={false} className="mb-6">
          <Form.Item
            label="Görsel Renk Seçimi"
            help="Renk seçiciden bir renk belirleyin veya HEX kodunu girin."
          >
            <Space align="start">
              <ColorPicker
               
                value={currentColorHex}
                onChange={handleColorPickerChange}
                showText
                format="hex"
                disabledAlpha
              />
              
            </Space>
          </Form.Item>
          {/* Formun hexCode alanı, ColorPicker tarafından güncellenir */}
    

          <Form.Item
            name="tcxCode"
            label="Pantone TCX Kodu (Opsiyonel)"
            help="Örn: 19-4052 TCX"
          >
            <Input placeholder="Pantone TCX kodu" />
          </Form.Item>
        </Card>

        <Card title="Ek Bilgiler" bordered={false} className="mb-6">
          <Form.Item
            name="notes"
            label="Notlar (Opsiyonel)"
          >
            <Input.TextArea rows={3} placeholder="Renk ile ilgili ek notlar" />
          </Form.Item>
        </Card>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </Button>
            <Button onClick={() => navigate('/colors')}>
              İptal
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EditColor;